import { Field } from 'formik';
import { useEffect } from 'react';
import moment from 'moment';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { datediff, parseDate } from '@shared/commonFunctions';
import { dateFormat } from '@shared/constants/formats';
import { FlexEnd, FormControl, FieldWrapper, FormContainer, FormWrapper } from '@styles/StyledDiv';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { colorTheme } from '@styles/muiTheme';
import { IAddAdjustment, IFinanceFormProps, IPayoffQuote } from '../interfaces';

export const FinanceForm = ({
  formTitle,
  formConfig,
  holidays,
  buttonText,
  formProps,
  salesPrice,
  disableSubmitButton = false,
  firstCustomButtonText = '',
  secondCustomButtonText = '',
  cancelButtonText = '',
  buttonWidth = '100px',
  handleFirstButtonClick,
  handleSecondButtonClick,
  handleCancelClick,
  handleAddAdjustment
}: IFinanceFormProps) => {

  const { values } = formProps;

  useEffect(() => {
    if (formTitle === 'Payoff Quote') {
      const tomorrow = new Date();
      const formattedGoodThruDate: string = moment(values.payOffData.payoffGoodDate).format(dateFormat);
      tomorrow.setDate(tomorrow.getDate() + 1); //financing will process payment the next business day
      //keep adding a day until it's not a Saturday, Sunday or a holiday)
      while (tomorrow.getDay() == 0 || tomorrow.getDay() == 6 || holidays?.includes((tomorrow.getMonth() + 1) + '/' + tomorrow.getDate())) {
        tomorrow.setDate(tomorrow.getDate() + 1);
      }
      const formattedTomorrowDate: string = moment(tomorrow).format(dateFormat);
      const diffDays = datediff(parseDate(formattedTomorrowDate), parseDate(formattedGoodThruDate));
      const perDiemValue = values.payOffData.payoffPerDiem ?? 0;
      const totalPayoffValue = ((values.payOffData?.dtTransactionFee || values.payOffData?.dtTaxes) ? (values.payOffData?.payoffAmount ?? 0) - (diffDays * perDiemValue): 0);
      const dtTotalDueValue = (totalPayoffValue + (values.payOffData?.dtTransactionFee ?? 0) + (values.payOffData?.dtTaxes ?? 0));
      let refundDue = (salesPrice as number) - totalPayoffValue;
      const dtRefundDueValue = values.payOffData?.atTitleIsReleasable ? (refundDue < 0 ? 0 : refundDue) : 0;
      let totalAdjustmentValue = 0;

      if (values.payOffData?.atTitleIsReleasable) {
        if (refundDue < 0.01) {
          const value = (salesPrice as number) - totalPayoffValue - dtRefundDueValue;
          totalAdjustmentValue = value < 0 ? -(value) : value;
          refundDue = 0;
        }
      }

      formProps.setFieldValue('payOffData.totalPayoffAmount', totalPayoffValue);
      formProps.setFieldValue('payOffData.dtTotalDue', dtTotalDueValue);
      formProps.setFieldValue('payOffData.customerRefundAmount', dtRefundDueValue);
      formProps.setFieldValue('payOffData.totalAdjustment', totalAdjustmentValue);
    }

    const totalAdjustment = (values.adjustments?.amountToFinanceFlat ?? 0) - (values.adjustments?.adjustedPayoff ?? 0);
    formProps.setFieldValue('adjustments.totalAdjustment', totalAdjustment);
  }, [
    values.payOffData.payoffPerDiem,
    values.payOffData.payoffAmount,
    values.payOffData.totalPayoffAmount,
    values.payOffData.payoffGoodDate,
    values.payOffData?.payoffAmount,
    values.payOffData?.atTitleIsReleasable,
    values.payOffData?.dtTransactionFee,
    values.payOffData?.dtTaxes,
    values.adjustments?.amountToFinanceFlat,
    values.adjustments?.adjustedPayoff,
    salesPrice,
    formTitle
  ]);

  const renderFields = (fieldsList: IFormFieldConfig<IPayoffQuote | IAddAdjustment>[]) => {
    return fieldsList?.map(({ name, label, component: Component, disabled = false, ...rest }) => (
      <FormControl key={name}>
        {label}
        <FieldWrapper>
          <Field {...rest} name={name} component={Component} disabled={disabled} />
        </FieldWrapper>
      </FormControl>
    ));
  };

  return (
    <>
      <Stack>
        <Typography variant="h2" marginBottom='15px' color={colorTheme.palette.common.black} >
          {formTitle}
        </Typography>
        <FormContainer>
          <FormWrapper>
            <Grid container spacing={12}>
              <Grid item md={12}>
                {renderFields(formConfig)}
              </Grid>
            </Grid>
            <FlexEnd>
              <CommonButtonWrapper direction="row">
                {cancelButtonText && (
                  <CancelButton
                    type='button'
                    width='64px'
                    onClick={handleCancelClick} />
                )}
                {secondCustomButtonText && (
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    sx={{ width: '172px', marginRight: '5px' }}
                    onClick={handleSecondButtonClick}>
                    {secondCustomButtonText}
                  </Button>
                )}
                {firstCustomButtonText && (
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    sx={{ width: '200px', marginRight: '5px' }}
                    onClick={handleFirstButtonClick}>
                    {firstCustomButtonText}
                  </Button>
                )}
                <SaveButton
                  type={handleAddAdjustment ? 'button' : 'submit'}
                  label={buttonText}
                  width={buttonWidth}
                  disabled={disableSubmitButton}
                  {...(handleAddAdjustment && { onClick: () => handleAddAdjustment(values.adjustments) })}
                />
              </CommonButtonWrapper>
            </FlexEnd>
          </FormWrapper>
        </FormContainer>
      </Stack>
    </>
  );
};
